import type { FieldIssue } from "../field-validation";
import "./validation.css";

export function focusValidationIssue(
  issue: Pick<FieldIssue, "targetId">,
): boolean {
  const target = document.getElementById(issue.targetId);
  if (!target) return false;
  for (
    let parent = target.parentElement;
    parent;
    parent = parent.parentElement
  ) {
    if (parent.tagName === "DETAILS")
      (parent as HTMLDetailsElement).open = true;
  }
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  const control = target.matches("input,select,textarea,button")
    ? target
    : target.querySelector<HTMLElement>(
        "input:not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled)",
      );
  const focusable = control || target;
  if (!control && !target.hasAttribute("tabindex"))
    target.setAttribute("tabindex", "-1");
  (focusable as HTMLElement).focus({ preventScroll: true });
  return true;
}

export function FieldError({ issue }: { issue?: FieldIssue }) {
  return issue ? (
    <p id={`${issue.targetId}-error`} className="field-validation-error">
      {issue.sectionLabel} — {issue.fieldLabel}: {issue.reason}
    </p>
  ) : null;
}

export function ValidationSummary({
  issues,
  onSelect,
  title = "Information to complete or correct",
}: {
  issues: FieldIssue[];
  onSelect?: (issue: FieldIssue) => void;
  title?: string;
}) {
  if (!issues.length) return null;
  const missing = issues.filter((issue) => issue.kind === "required").length;
  const invalid = issues.filter((issue) => issue.kind === "invalid").length;
  return (
    <section className="validation-summary" aria-label={title}>
      <h3>{title}</h3>
      <p
        className="validation-counts"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {missing > 0 && (
          <span>
            {missing} required {missing === 1 ? "item" : "items"} missing
          </span>
        )}
        {invalid > 0 && (
          <span>
            {invalid} invalid {invalid === 1 ? "value" : "values"}
          </span>
        )}
      </p>
      <ul>
        {issues.map((issue) => (
          <li key={issue.id}>
            <button
              type="button"
              onClick={() =>
                onSelect ? onSelect(issue) : focusValidationIssue(issue)
              }
            >
              <strong>
                {issue.sectionLabel} — {issue.fieldLabel}
              </strong>
              <span>{issue.reason}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
